import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import Card from "@mui/material/Card";
import Loader from "components/Progress";
import Nodata from "assets/img/pieChart.png";
import AlarmHistoryChart from "components/Charts/Alarm History";
import {
  fillPeriods,
  pickInterval,
} from "components/Charts/Alarm History/fillPeriods";
import { useGetAlarmSeriesQuery } from "../../../services/alarms";
import { alarmsQuery } from "../List";

// The alarm log keeps 31 days, so the chart never starts earlier.
const LOG_DAYS = 31;

// "Alarm raises" (2026-10-30): one line per priority over the chosen range, each
// point the number of raises in that hour or day, from the alarm log. Full width
// in the Timeline view; beside the two count cards in the List view, in place of
// the old two-week trend (owner, 2026-10-30), where `list` adds that the status
// filter (which counts alarms, not raises) does not apply.
export default function RaisesChart({ serviceId, list }) {
  let token = window.localStorage.getItem("token");
  const metaDataValue = useSelector((state) => state.metaData);
  const alarmsFilter = useSelector((state) => state.alarmsFilter);

  // The chosen range with the start no earlier than 31 days ago, as epoch
  // milliseconds. Worked out once per chosen range, so the request and the
  // chart stay the same between renders.
  const { start, end, interval, clamped } = useMemo(() => {
    const earliest = new Date();
    earliest.setDate(earliest.getDate() - LOG_DAYS);
    const chosen = new Date(alarmsFilter.date.startTime).getTime();
    const from = Math.max(chosen, earliest.getTime());
    // no empty periods drawn after now
    const to = Math.min(new Date(alarmsFilter.date.endTime).getTime(), Date.now());
    return {
      start: from,
      end: to,
      interval: pickInterval(from, to),
      clamped: chosen < earliest.getTime(),
    };
  }, [alarmsFilter.date.startTime, alarmsFilter.date.endTime]);
  // the whole chosen range is older than the log keeps: nothing to ask for
  // (code review 2026-10-30)
  const outsideLog = start >= end;

  // the page's solutions, priority and search, counted in the browser's zone
  const series = useGetAlarmSeriesQuery(
    {
      token,
      params: `?${alarmsQuery(alarmsFilter, {
        serviceId,
        services: metaDataValue.services,
        withStatus: false,
        dates: { startTime: start, endTime: end },
      })}&interval=${interval}&timezone=${encodeURIComponent(
        Intl.DateTimeFormat().resolvedOptions().timeZone
      )}`,
    },
    { skip: outsideLog }
  );

  // currentData: this request's result only; the server returns only the
  // periods that have raises, so the empty ones are filled with zeros here
  const payload = series.currentData?.payload;
  const data = useMemo(
    () => fillPeriods(payload, start, end, interval),
    [payload, start, end, interval]
  );

  let body;
  if (outsideLog) {
    body = (
      <p style={{ color: "#9f9f9f", fontSize: "13px" }}>
        The timeline covers the last 31 days.
      </p>
    );
  } else if (series.isFetching) {
    body = (
      <span
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
        }}
      >
        <Loader />
      </span>
    );
  } else if (series.isError) {
    body = (
      <p style={{ color: "#9f9f9f", fontSize: "13px" }}>
        Could not load the alarm raises.
      </p>
    );
  } else if (!(payload || []).length) {
    body = (
      <div
        style={{
          height: "100%",
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          color: "#c8c8c8",
        }}
      >
        <img src={Nodata} height="100px" width="100px" />
        <p>No alarm raises in this period</p>
      </div>
    );
  } else {
    // the end is exclusive: the axis stops just before it, so no empty period
    // is drawn after the last (code review 2026-10-30)
    body = (
      <AlarmHistoryChart
        mode="lines"
        data={data}
        interval={interval}
        start={start}
        end={end - 1}
        height={140}
      />
    );
  }

  // the top row's height, padding and margins. In the List view it fills the rest of
  // the row beside the count cards; in the Timeline view it sits in the page's column
  // and must not grow (it took half the height there, 2026-10-30)
  return (
    <Card
      style={{
        minHeight: "215px",
        position: "relative",
        padding: "16px",
        margin: "10px 0px 16px 0px",
        ...(list ? { flex: 1, minWidth: 0 } : {}),
      }}
    >
      <p
        style={{
          color: "#bfbec8",
          fontSize: "15px",
          display: "flex",
          justifyContent: "space-between",
          gap: "10px",
        }}
      >
        <b>{`Alarm raises${outsideLog ? "" : ` ( per ${interval} )`}`}</b>
        <span style={{ fontSize: "12px", color: "#9f9f9f" }}>
          {`Counts each time an alarm was raised${
            list ? "; the status and show-only filters do not apply" : ""
          }${clamped ? "; the last 31 days only" : ""}`}
        </span>
      </p>
      {body}
    </Card>
  );
}
